"use client";

import { useEffect, useState } from "react";

type Prefs = { necessary: true; performance: boolean; social: boolean };

const COOKIE_NAME = "cookie_consent";
const ONE_YEAR = 60 * 60 * 24 * 365;

function readConsent(): Prefs | null {
  if (typeof document === "undefined") return null;
  const hit = document.cookie.split("; ").find((c) => c.startsWith(COOKIE_NAME + "="));
  if (hit === undefined) return null;
  try {
    const raw = decodeURIComponent(hit.split("=").slice(1).join("="));
    const parsed = JSON.parse(raw);
    return { necessary: true, performance: parsed.performance === true, social: parsed.social === true };
  } catch {
    return null;
  }
}

function writeConsent(p: Prefs) {
  const payload = { necessary: true, performance: p.performance, social: p.social, ts: new Date().toISOString() };
  document.cookie = COOKIE_NAME + "=" + encodeURIComponent(JSON.stringify(payload)) + "; path=/; max-age=" + ONE_YEAR + "; samesite=lax";
  window.dispatchEvent(new CustomEvent("shimesu-consent", { detail: payload }));
}

function Toggle({ on, locked, onChange, label }: { on: boolean; locked?: boolean; onChange: (v: boolean) => void; label: string }) {
  const active = locked === true ? true : on;
  return (
    <button
      type="button"
      role="switch"
      aria-checked={active}
      aria-label={label}
      disabled={locked === true}
      onClick={() => onChange(active === false)}
      className={
        "relative w-[38px] h-[21px] rounded-full transition-colors shrink-0 " +
        (active ? "bg-sh-red" : "bg-black/20") +
        (locked === true ? " opacity-60 cursor-not-allowed" : " cursor-pointer")
      }
    >
      <span
        className={
          "absolute top-[3px] w-[15px] h-[15px] bg-white rounded-full transition-all " +
          (active ? "left-[20px]" : "left-[3px]")
        }
      />
    </button>
  );
}

export function CookieConsent({ initiallyOpen = false }: { initiallyOpen?: boolean }) {
  const [open, setOpen] = useState(initiallyOpen);
  const [managing, setManaging] = useState(false);
  const [perf, setPerf] = useState(false);
  const [social, setSocial] = useState(false);

  useEffect(() => {
    const existing = readConsent();
    if (existing === null) {
      setOpen(true);
    } else {
      setPerf(existing.performance);
      setSocial(existing.social);
      setOpen(false);
    }
    const reopen = () => {
      const e = readConsent();
      if (e !== null) { setPerf(e.performance); setSocial(e.social); }
      setManaging(true);
      setOpen(true);
    };
    window.addEventListener("shimesu-open-cookie-prefs", reopen);
    return () => window.removeEventListener("shimesu-open-cookie-prefs", reopen);
  }, []);

  if (open === false) return null;

  const close = () => { setOpen(false); setManaging(false); };
  const acceptAll = () => { writeConsent({ necessary: true, performance: true, social: true }); close(); };
  const rejectAll = () => { writeConsent({ necessary: true, performance: false, social: false }); close(); };
  const acceptSelection = () => { writeConsent({ necessary: true, performance: perf, social: social }); close(); };

  const btnSolid = "font-sans text-[12px] font-semibold bg-sh-red text-white py-2.5 px-5 rounded-3xl border-0 cursor-pointer";
  const btnGhost = "font-sans text-[12px] font-semibold bg-transparent text-white border border-white/30 py-2.5 px-5 rounded-3xl cursor-pointer";
  const btnGhostDark = "font-sans text-[12px] font-semibold bg-transparent text-sh-tx border border-black/15 py-2.5 px-5 rounded-3xl cursor-pointer";

  if (managing) {
    return (
      <div className="fixed inset-0 z-[200] flex items-end sm:items-center justify-center bg-black/40 px-4 py-6">
        <div className="bg-white rounded-lg max-w-[620px] w-full max-h-[85vh] overflow-y-auto p-6 md:p-8">
          <h2 className="font-serif text-[1.15rem] font-normal text-sh-tx mb-3">Cookie Categories</h2>
          <p className="font-sans text-[13px] text-sh-tx2 leading-relaxed mb-6">
            We use cookies to help you navigate efficiently and perform certain functions. You will find
            detailed information about all cookies under each consent category below. The cookies that are
            categorised as necessary are stored on your browser as they are essential for enabling the basic
            functionalities of the site.
          </p>

          <div className="space-y-5 border-t border-black/[0.08] pt-5">
            <div className="flex gap-4 items-start">
              <Toggle on={true} locked onChange={() => {}} label="Functional cookies" />
              <div>
                <h3 className="font-sans text-[13px] font-semibold text-sh-tx mb-1">Functional cookies</h3>
                <p className="font-sans text-[12px] text-sh-tx2 leading-relaxed">
                  Functional cookies are required for basic site functionality and are therefore always enabled.
                  Without these cookies the website cannot function properly. They remember the country and
                  language you selected.
                </p>
              </div>
            </div>

            <div className="flex gap-4 items-start">
              <Toggle on={perf} onChange={setPerf} label="Performance cookies" />
              <div>
                <h3 className="font-sans text-[13px] font-semibold text-sh-tx mb-1">Performance cookies</h3>
                <p className="font-sans text-[12px] text-sh-tx2 leading-relaxed">
                  We use a third-party web analytics tool to collect information about how visitors use and
                  interact with our site. These cookies collect information about the number of visitors to our
                  site, the pages or content with the most traffic, approximately where in the world you are
                  visiting the site from, and how you found our site. We use this information to help improve
                  the site.
                </p>
              </div>
            </div>

            <div className="flex gap-4 items-start">
              <Toggle on={social} onChange={setSocial} label="Social media cookies" />
              <div>
                <h3 className="font-sans text-[13px] font-semibold text-sh-tx mb-1">Social media cookies</h3>
                <p className="font-sans text-[12px] text-sh-tx2 leading-relaxed">
                  Sometimes we pair up with social media partners or with online advertising platforms who drop
                  cookies on your device when you visit our site in order to remember that you have interacted
                  with any of our websites. These can be considered third-party cookies. X and LinkedIn do not
                  use cookies, but may collect anonymous data about your web activity.
                </p>
              </div>
            </div>
          </div>

          <div className="flex flex-wrap gap-2.5 mt-7 pt-5 border-t border-black/[0.08]">
            <button type="button" onClick={acceptAll} className={btnSolid}>Accept All</button>
            <button type="button" onClick={rejectAll} className={btnGhostDark}>Reject All</button>
            <button type="button" onClick={acceptSelection} className={btnGhostDark}>Accept current selection</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed bottom-0 left-0 right-0 z-[200] bg-[#1c1d20] px-6 md:px-14 py-5">
      <div className="max-w-[960px] mx-auto flex flex-col lg:flex-row lg:items-center gap-4 lg:gap-8">
        <div className="flex-1">
          <h2 className="font-serif text-[15px] font-normal text-white mb-1.5">We Value Your Privacy</h2>
          <p className="font-sans text-[12px] text-white/70 leading-relaxed">
            We use cookies on our website to give you the most relevant experience by remembering your
            preferences and repeat visits. By clicking Accept All, you consent to the use of all the cookies.
            However, you may manage individual preferences to provide a controlled consent. See our{" "}
            <a href="/privacy" className="text-white underline">Privacy Policy</a>.
          </p>
        </div>
        <div className="flex flex-wrap gap-2.5 shrink-0">
          <button type="button" onClick={acceptAll} className={btnSolid}>Accept All</button>
          <button type="button" onClick={rejectAll} className={btnGhost}>Reject All</button>
          <button type="button" onClick={() => setManaging(true)} className={btnGhost}>Manage Preferences</button>
        </div>
      </div>
    </div>
  );
}

export function CookiePrefsLink({ className }: { className?: string }) {
  return (
    <button
      type="button"
      onClick={() => window.dispatchEvent(new CustomEvent("shimesu-open-cookie-prefs"))}
      className={className || "font-sans text-[11px] text-sh-tx3 bg-transparent border-0 p-0 cursor-pointer hover:text-sh-tx2"}
    >
      Cookie preferences
    </button>
  );
}
